import { useMemo, useState } from "react";
import { Check, CloudUpload, Unlink, X } from "lucide-react";
import { formatCurrency } from "../cashflow/CashflowContext";
import { formatAppDate } from "../../utils/date";
import type { Currency } from "@/types";
import type { BankReconciliationMatch } from "@/types/bankReconciliation";

type Props = {
  matches: BankReconciliationMatch[];
  onUnmatch: (matchId: string) => void;
  onPush: (matchIds: string[]) => Promise<void>;
};

/**
 * Reviewed matches with per-row selection and an ERP push action.
 * The push button adapts: with no selection it targets every unpushed
 * match, with a selection only those — approve all at once or just some.
 */
export function ReconciledMatchesList({ matches, onUnmatch, onPush }: Props) {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [confirming, setConfirming] = useState(false);
  const [isPushing, setIsPushing] = useState(false);

  const unpushed = useMemo(
    () => matches.filter((match) => !match.erpPushedAt),
    [matches],
  );
  const pushedCount = matches.length - unpushed.length;
  // Selection can go stale after a refresh — only count ids still unpushed.
  const validSelection = useMemo(
    () =>
      new Set(
        [...selectedIds].filter((id) =>
          unpushed.some((match) => match.id === id),
        ),
      ),
    [selectedIds, unpushed],
  );
  const pushTargets =
    validSelection.size > 0
      ? unpushed.filter((match) => validSelection.has(match.id))
      : unpushed;

  function toggleOne(matchId: string) {
    setConfirming(false);
    setSelectedIds((previous) => {
      const next = new Set(previous);
      if (next.has(matchId)) {
        next.delete(matchId);
      } else {
        next.add(matchId);
      }
      return next;
    });
  }

  function toggleAll() {
    setConfirming(false);
    setSelectedIds(
      validSelection.size === unpushed.length
        ? new Set()
        : new Set(unpushed.map((match) => match.id)),
    );
  }

  async function handlePush() {
    if (pushTargets.length === 0) return;
    setIsPushing(true);
    try {
      await onPush(pushTargets.map((match) => match.id));
      setSelectedIds(new Set());
    } finally {
      setIsPushing(false);
      setConfirming(false);
    }
  }

  if (matches.length === 0) {
    return (
      <p className="px-4 py-8 text-center text-sm text-slate-500">
        Nothing reconciled yet. Accept an AI match or drag a journal entry
        onto a bank transaction.
      </p>
    );
  }

  const pushLabel =
    validSelection.size > 0
      ? `Push ${validSelection.size} selected to ERP`
      : `Push all ${unpushed.length} to ERP`;

  return (
    <div className="flex min-h-0 flex-col">
      <div className="flex flex-wrap items-center gap-3 border-b border-slate-200 px-4 py-2.5">
        <label className="inline-flex cursor-pointer items-center gap-2 text-xs font-medium text-slate-600">
          <input
            type="checkbox"
            checked={
              unpushed.length > 0 && validSelection.size === unpushed.length
            }
            disabled={unpushed.length === 0}
            onChange={toggleAll}
            className="h-4 w-4 rounded border-slate-300 accent-emerald-600"
            aria-label="Select all matches awaiting push"
          />
          {validSelection.size > 0
            ? `${validSelection.size} of ${unpushed.length} selected`
            : "Select all"}
        </label>

        <span className="text-xs text-slate-400">
          {pushedCount > 0
            ? `${pushedCount} of ${matches.length} in ERP`
            : "None pushed to ERP yet"}
        </span>

        <div className="ml-auto flex items-center gap-2">
          {isPushing ? (
            <span className="text-xs font-medium text-slate-500">
              Pushing {pushTargets.length} to ERP…
            </span>
          ) : confirming ? (
            <>
              <span className="text-xs font-medium text-slate-600">
                Push {pushTargets.length}{" "}
                {pushTargets.length === 1 ? "match" : "matches"} to your
                accounting software?
              </span>
              <button
                type="button"
                onClick={() => void handlePush()}
                className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-emerald-700"
              >
                <Check className="h-3.5 w-3.5" strokeWidth={3} aria-hidden />
                Confirm push
              </button>
              <button
                type="button"
                onClick={() => setConfirming(false)}
                aria-label="Cancel push"
                className="inline-flex items-center rounded-lg border border-slate-200 p-1.5 text-slate-500 transition hover:bg-slate-50"
              >
                <X className="h-3.5 w-3.5" aria-hidden />
              </button>
            </>
          ) : unpushed.length > 0 ? (
            <button
              type="button"
              onClick={() => setConfirming(true)}
              className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-emerald-700"
            >
              <CloudUpload className="h-3.5 w-3.5" aria-hidden />
              {pushLabel}
            </button>
          ) : (
            <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-600">
              <Check className="h-3.5 w-3.5" aria-hidden />
              All matches are in your ERP
            </span>
          )}
        </div>
      </div>

      <ul className="max-h-[30rem] divide-y divide-slate-100 overflow-y-auto">
        {matches.map((match) => {
          const currency = match.bankTransaction.currency as Currency;
          const isPushed = Boolean(match.erpPushedAt);
          return (
            <li
              key={match.id}
              className={`flex flex-wrap items-center gap-3 px-4 py-3 text-sm ${
                validSelection.has(match.id) ? "bg-emerald-50/40" : ""
              }`}
            >
              {isPushed ? (
                <span className="w-4 shrink-0" aria-hidden />
              ) : (
                <input
                  type="checkbox"
                  checked={validSelection.has(match.id)}
                  onChange={() => toggleOne(match.id)}
                  aria-label={`Select match for ${match.bankTransaction.description}`}
                  className="h-4 w-4 shrink-0 rounded border-slate-300 accent-emerald-600"
                />
              )}

              <div className="min-w-0 flex-1">
                <p className="truncate font-medium text-slate-900">
                  {match.bankTransaction.description}
                </p>
                <p className="text-xs text-slate-500">
                  {formatAppDate(match.bankTransaction.date)} ·{" "}
                  {formatCurrency(match.bankTransaction.amount, currency)}
                </p>
              </div>
              <Check
                className="h-4 w-4 shrink-0 text-emerald-600"
                aria-hidden
              />
              <div className="min-w-0 flex-1 text-right">
                <p className="truncate font-medium text-slate-900">
                  {match.journalEntry.description}
                </p>
                <p className="text-xs text-slate-500">
                  {formatAppDate(match.journalEntry.date)} ·{" "}
                  {formatCurrency(match.journalEntry.amount, currency)}
                </p>
              </div>

              {isPushed ? (
                <span
                  title={
                    match.erpPushedAt
                      ? `Pushed ${formatAppDate(match.erpPushedAt)}`
                      : undefined
                  }
                  className="inline-flex shrink-0 items-center gap-1 rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-medium text-emerald-700"
                >
                  <CloudUpload className="h-3.5 w-3.5" aria-hidden />
                  In ERP{match.erpReference ? ` · ${match.erpReference}` : ""}
                </span>
              ) : (
                <button
                  type="button"
                  onClick={() => onUnmatch(match.id)}
                  className="inline-flex shrink-0 items-center gap-1 rounded-md border border-slate-200 px-2 py-1 text-xs font-medium text-slate-600 hover:bg-slate-50"
                >
                  <Unlink className="h-3.5 w-3.5" aria-hidden />
                  Unmatch
                </button>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
